import {
    getCategories,
    getCategoryPrices,
    getCategoryOffers,
    createStream,
    getStream,
    getPurchases,
    getPurchase,
    purchasePackage,
    getStreams,
    updateStream,
    trainStream,
    getMonthlyLeadStatus,
    cancelMonthlyLeadSubscription,
    changeMonthlyLeadAllocation,
} from './agent';

import { parseStreamList } from './streamList';
import { completePackagePrices } from './packagePrices';

import type {
    LeadCategory,
    LeadPackagePrice,
    LeadPricingOffer,
    CreateStreamRequest,
    Stream,
    UpdateStreamRequest,
    StreamTrainingExample,
    LeadStreamFilterExample,
    LeadStreamFilterProfile,
    PurchaseRequest,
    PurchaseResponse,
    LeadPackagePurchase,
    MonthlyLeadStatus,
} from './agent';

class LeadStreamsService {
    queryKey = 'lead-streams';

    trainStream = async (streamId: number, updatedAt: string, examples: StreamTrainingExample[]): Promise<Stream> => {
        try {
            return (await trainStream(streamId, updatedAt, examples)).data;
        } catch (error: any) {
            if (error?.response?.status !== 404) throw error;

            // The local preview may use an API version without the dedicated training action.
            const current = (await getStream(streamId)).data;
            if (!current.package_size) throw error;
            if (current.updated_at !== updatedAt) {
                throw Object.assign(new Error('Правила потока изменились'), { response: { status: 409 } });
            }

            const profile: LeadStreamFilterProfile = current.filter_profile || {};
            const submittedIds = new Set(examples.map((example) => example.id));
            const keepOthers = (items?: LeadStreamFilterExample[]) => (items || []).filter(
                (item) => !submittedIds.has(Number(item.message_id ?? item.id))
            );
            const chosen = (choice: StreamTrainingExample['choice']): LeadStreamFilterExample[] => examples
                .filter((example) => example.choice === choice)
                .map((example) => ({
                    id: example.id,
                    message_id: example.id,
                    text: example.text.trim().slice(0, 260),
                    ...(choice === 'skip' && example.reason ? { reason: example.reason } : {}),
                }));
            return (await updateStream(streamId, {
                filter_profile: {
                    ...profile,
                    positive_examples: [...chosen('fit'), ...keepOthers(profile.positive_examples)],
                    negative_examples: [...chosen('skip'), ...keepOthers(profile.negative_examples)],
                    maybe_examples: [...chosen('maybe'), ...keepOthers(profile.maybe_examples)],
                },
            })).data;
        }
    };

    getPurchase = async (id: number): Promise<LeadPackagePurchase> => (await getPurchase(id)).data;

    getMonthlyLeadStatus = async (streamId: number): Promise<MonthlyLeadStatus> => {
        try {
            return (await getMonthlyLeadStatus(streamId)).data;
        } catch (error: any) {
            if (error?.response?.status === 404) return { available: false, subscription: null };
            throw error;
        }
    };

    cancelMonthlyLeadSubscription = async (streamId: number): Promise<MonthlyLeadStatus> =>
        (await cancelMonthlyLeadSubscription(streamId)).data;

    changeMonthlyLeadAllocation = async (streamId: number, packageSize: number): Promise<MonthlyLeadStatus> =>
        (await changeMonthlyLeadAllocation(streamId, packageSize)).data;

    getCategories = async (): Promise<LeadCategory[]> => {
        try {
            const { data } = await getCategories();
            // Обрабатываем случай, когда API возвращает объект с results (пагинация)
            if (data && typeof data === 'object' && 'results' in data && Array.isArray((data as any).results)) {
                return Promise.resolve((data as any).results);
            }
            // Или просто массив
            if (Array.isArray(data)) {
                return Promise.resolve(data);
            }
            return Promise.resolve([]);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getCategoryPrices = async (categoryId: number): Promise<LeadPackagePrice[]> => {
        try {
            const { data } = await getCategoryPrices(categoryId);
            return Promise.resolve(completePackagePrices(data));
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getCategoryOffers = async (categoryId: number): Promise<LeadPricingOffer[]> => {
        try {
            const { data } = await getCategoryOffers(categoryId);
            return Promise.resolve(data);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getStreams = async (options?: { signal?: AbortSignal }): Promise<Stream[]> => {
        const { data } = await getStreams(options?.signal);
        return parseStreamList(data);
    };

    getPurchases = async (): Promise<LeadPackagePurchase[]> => {
        try {
            const { data } = await getPurchases();
            if (data && typeof data === 'object' && 'results' in data && Array.isArray((data as any).results)) {
                return Promise.resolve((data as any).results);
            }
            if (Array.isArray(data)) {
                return Promise.resolve(data);
            }
            return Promise.resolve([]);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    createStream = async (data: CreateStreamRequest): Promise<Stream> => {
        try {
            const { data: response } = await createStream(data);
            return Promise.resolve(response);
        } catch (error: any) {
            // A saved draft can refer to a project that no longer belongs to this account.
            // The stream itself does not require a project; keep its search profile and retry once.
            const projectErrors = error?.response?.data?.project;
            if (
                data.project != null &&
                error?.response?.status === 400 &&
                Array.isArray(projectErrors) &&
                projectErrors.some((message: unknown) =>
                    typeof message === 'string' && /^Invalid pk .+ - object does not exist\.$/.test(message)
                )
            ) {
                const { project: _staleProject, ...streamWithoutProject } = data;
                const { data: response } = await createStream(streamWithoutProject);
                return response;
            }
            throw error;
        }
    };

    getStream = async (streamId: number, signal?: AbortSignal): Promise<Stream> => {
        try {
            const { data } = await getStream(streamId, signal);
            return Promise.resolve(data);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    updateStream = async (streamId: number, data: UpdateStreamRequest): Promise<Stream> => {
        try {
            const { data: response } = await updateStream(streamId, data);
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    purchasePackage = async (streamId: number, data: PurchaseRequest): Promise<PurchaseResponse> => {
        try {
            const { data: response } = await purchasePackage(streamId, data);
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };
}

export default new LeadStreamsService();
